/// <reference types="google.maps" />

'use client'

import { memo, useCallback, useMemo } from 'react'
import { AdvancedMarker, useAdvancedMarkerRef } from '@vis.gl/react-google-maps'
import type { MapLocatorMarkerSettings, MapLocatorTag } from 'ecosystem'
import type { MapLocatorEntry } from './useMapLocator'
import { markerSvg } from './markerSvg'
import { DEFAULT_MARKER_BORDER_COLOR, MAP_LOCATOR_CN, mapLocatorCn as cn } from './constants'
import { itemPosition, resolveMarkerIcon, tagColor } from './utils'

type MarkerInstance = google.maps.marker.AdvancedMarkerElement

interface LocationMarkerProps {
  item: MapLocatorEntry
  tags: MapLocatorTag[]
  markers: MapLocatorMarkerSettings
  isActive: boolean
  onSelect: (item: MapLocatorEntry) => void
  /** Registers the underlying element with the clusterer and the info-window anchor map */
  onRegister: (id: string, marker: MarkerInstance | null) => void
}

const LocationMarker = ({
  item,
  tags,
  markers,
  isActive,
  onSelect,
  onRegister
}: LocationMarkerProps) => {
  const [markerRef] = useAdvancedMarkerRef()

  const handleRef = useCallback(
    (marker: MarkerInstance | null) => {
      markerRef(marker)
      onRegister(item.id, marker)
    },
    [markerRef, onRegister, item.id]
  )

  const handleClick = useCallback(() => onSelect(item), [onSelect, item])

  const iconUrl = resolveMarkerIcon(item, tags, markers.defaultIconUrl)

  // The same geometry as the cluster badge, with a hollow ring where the count goes
  const svg = useMemo(
    () =>
      markerSvg({
        fill: markers.useTagColors
          ? tagColor(tags, item.tagIds?.[0])
          : markers.color || tagColor(tags),
        borderColor: markers.borderColor || DEFAULT_MARKER_BORDER_COLOR,
        borderWidth: markers.borderWidth ?? 2,
        glyph: { type: 'ring' },
        shape: markers.shape ?? 'PIN'
      }),
    [markers, tags, item.tagIds]
  )

  return (
    <AdvancedMarker
      onClick={handleClick}
      position={itemPosition(item)}
      ref={handleRef}
      title={item.name}
      zIndex={isActive ? 2 : 1}
    >
      {iconUrl ? (
        <img
          alt=""
          height={markers.height ?? 40}
          src={iconUrl}
          style={{ transform: isActive ? 'scale(1.15)' : undefined }}
          width={markers.width ?? 40}
        />
      ) : (
        <div
          className={cn(MAP_LOCATOR_CN.MARKER)}
          // Generated here from settings-supplied colours, which markerSvg filters;
          // no merchant free text reaches the DOM.
          dangerouslySetInnerHTML={{ __html: svg }}
          style={{
            lineHeight: 0,
            transform: isActive ? 'scale(1.15)' : undefined,
            transformOrigin: 'bottom center'
          }}
        />
      )}
    </AdvancedMarker>
  )
}

export default memo(LocationMarker)
